/**
 * What a comment has attached, below its text: a report (an agent's, posted with it) shown in place — a PDF's first
 * page as printed (all of it a click away), Markdown formatted — a document or spreadsheet in Revisa's viewer, and any
 * other item as a card. Each opens its item (a quick look on a plain click, as items' chips do).
 */

import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { AttachmentOut, DocumentOut, ItemDetail, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { docTypeOf, readsAsPdf } from "../../lib/documents.ts";
import { outsideOf, spaceApi, spaceUrl } from "../../lib/paths.ts";
import { peek } from "../../lib/peek.ts";
import { useAction } from "../../lib/resource.ts";
import { isPlainClick, navigate } from "../../lib/router.ts";
import { titleOf } from "../../lib/titles.ts";
import { InlineButton, Link } from "../../ui/Button.tsx";
import { Badge } from "../../ui/misc.tsx";
import { PdfViewer } from "../../ui/pdf/PdfViewer.tsx";
import { DocumentDownload, DocumentPreview } from "../item/document.tsx";
import { KindIcon } from "../items/icons.tsx";
import { Markdown } from "./markdown.tsx";

/** Where an attachment opens: its page in the space; from outside, its own page there (if any). */
function hrefOf(space: SpaceOut, a: AttachmentOut): string | null {
  if (outsideOf(space)) {
    return a.outside ?? null;
  }
  return spaceUrl(space, `/items/${a.id}`);
}

/** Opens `a`: a quick look beside the page on a plain click (inside the space), its page otherwise. */
function Opens({
  space,
  a,
  class: cls,
  label,
  children,
}: {
  space: SpaceOut;
  a: AttachmentOut;
  class: string;
  label?: string;
  children: ComponentChildren;
}) {
  const href = hrefOf(space, a);
  if (!href) {
    return <div class={cls}>{children}</div>;
  }
  return (
    <Link
      href={href}
      aria-label={label}
      class={cls}
      onClick={(e) => {
        if (isPlainClick(e) && !outsideOf(space)) {
          e.preventDefault();
          peek(a.id, e.currentTarget);
        }
      }}
    >
      {children}
    </Link>
  );
}

/** Its kind's icon, its title, and "Report" for one posted with the comment. */
function Heading({ a }: { a: AttachmentOut }) {
  return (
    <span class="flex min-w-0 items-center gap-2">
      {a.kind ? <KindIcon item={{ kind: a.kind, file: a.file ?? null, title: a.title }} /> : null}
      <span class="truncate font-medium text-zinc-900">{titleOf(a)}</span>
      {a.posted ? <Badge tone="gray">{t("attached.report")}</Badge> : null}
    </span>
  );
}

/** A PDF report: its first page as printed (bare), or every page once asked; clicking it opens the item. */
function PdfReport({ space, a, url }: { space: SpaceOut; a: AttachmentOut; url: string }) {
  const [pages, setPages] = useState(0);
  const [all, setAll] = useState(false);
  return (
    <div class="flex flex-col gap-1">
      <Opens
        space={space}
        a={a}
        label={titleOf(a)}
        class="block overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 transition-shadow hover:shadow-md"
      >
        <PdfViewer url={url} title={titleOf(a)} bare={true} limit={all ? pages : 1} onPages={setPages} />
      </Opens>
      {pages > 1 ? (
        <span>
          <InlineButton size="xs" onClick={() => setAll(!all)} aria-expanded={all}>
            {all ? t("attached.firstPage") : t("attached.allPages", { count: pages })}
          </InlineButton>
        </span>
      ) : null}
    </div>
  );
}

/**
 * A Markdown report as a document: downloaded as Word or PDF (the server converts it), or copied as a document of
 * Revisa's own to edit ("Edit as document"), seen as the report is (private when it's private), opened for editing.
 */
function AsDocument({ space, a }: { space: SpaceOut; a: AttachmentOut }) {
  const [copy, copying] = useAction(async () => {
    const [doc, item] = await Promise.all([
      api.get<DocumentOut>(spaceApi(space, `/items/${a.id}/document`)),
      api.get<ItemDetail>(spaceApi(space, `/items/${a.id}`)),
    ]);
    const made = await api.post<ItemDetail>(spaceApi(space, "/files/new"), {
      kind: "doc",
      title: titleOf(a),
      content: doc.content,
      private: item.visibility === "private",
    });
    navigate(spaceUrl(space, `/items/${made.id}?edit=1`));
  });
  return (
    <>
      <DocumentDownload
        space={space}
        id={a.id}
        kind="doc"
        native={false}
        original={a.url ? { url: a.url, ext: ".md", office: false } : undefined}
      />
      <InlineButton size="xs" disabled={copying} onClick={() => copy()}>
        {t("reports.editAsDocument")}
      </InlineButton>
    </>
  );
}

/** A Markdown (or plain text) report: formatted, its start, the rest a click away. */
function TextReport({ space, a, text }: { space: SpaceOut; a: AttachmentOut; text: string }) {
  const [open, setOpen] = useState(false);
  const markdown = a.file?.mime === "text/markdown";
  return (
    <div class="flex flex-col gap-1">
      <div
        class={`relative overflow-hidden rounded-md border border-zinc-200 bg-white p-3 ${open ? "" : "max-h-80"}`}
        data-report="text"
      >
        {markdown ? (
          <Markdown space={space} text={text} />
        ) : (
          <pre class="font-sans text-sm whitespace-pre-wrap">{text}</pre>
        )}
        {open ? null : <div class="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white" />}
      </div>
      <span class="flex flex-wrap gap-x-4">
        <InlineButton size="xs" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t("attached.less") : t("attached.more")}
        </InlineButton>
        {a.more ? (
          <Opens space={space} a={a} class="text-xs font-medium text-blue-700 hover:underline">
            {t("attached.open")}
          </Opens>
        ) : null}
        {markdown && !outsideOf(space) ? <AsDocument space={space} a={a} /> : null}
      </span>
    </div>
  );
}

function Attachment({ space, a }: { space: SpaceOut; a: AttachmentOut }) {
  if (a.hidden || a.missing) {
    return (
      <li class="rounded-md border border-dashed border-zinc-200 px-3 py-2 text-xs text-zinc-500">
        {t(a.hidden ? "attached.hidden" : "attached.missing")}
      </li>
    );
  }
  const type = a.file ? docTypeOf(a.file.mime) : null;
  // A PDF, or a document's or presentation's made one (a spreadsheet stays in Revisa's viewer).
  const made = a.pdf_url && (type === null || readsAsPdf(type)) ? a.pdf_url : null;
  const pdf = a.file?.mime === "application/pdf" && a.url ? a.url : made;
  const text = a.text ?? null;
  const doc = a.file && type && !outsideOf(space) ? a.file : null;
  const shown = pdf ? (
    <PdfReport space={space} a={a} url={pdf} />
  ) : doc ? (
    // A document or spreadsheet: its start, in a box (the rest on its page); what can't be shown, nothing.
    <div class="max-h-[28rem] overflow-hidden" data-report="document">
      <DocumentPreview
        space={space}
        shown={{ id: a.id, title: titleOf(a), file: doc, url: a.url ?? undefined, attached: true }}
        fallback={null}
      />
    </div>
  ) : text !== null && a.posted ? (
    <TextReport space={space} a={a} text={text} />
  ) : null;
  return (
    <li class="flex flex-col gap-1.5" data-attachment={a.id}>
      <Opens
        space={space}
        a={a}
        class={
          shown
            ? "flex items-center text-sm hover:underline"
            : "flex items-center rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm hover:bg-zinc-50"
        }
      >
        <Heading a={a} />
      </Opens>
      {shown}
    </li>
  );
}

/** A comment's attachments, in its order. */
export function Attached({ space, list }: { space: SpaceOut; list: readonly AttachmentOut[] | undefined }) {
  if (!list?.length) {
    return null;
  }
  return (
    <ul aria-label={t("attached.label")} class="mt-1 flex max-w-xl flex-col gap-3">
      {list.map((a) => (
        <Attachment key={a.id} space={space} a={a} />
      ))}
    </ul>
  );
}
